import { Ico, SvgrIcon } from "@/assets/icons";

export interface Money {
  amount: number;
  currency: string;
}

export type BillingInterval =
  | "day"
  | "week"
  | "month"
  | "quarter"
  | "half-year"
  | "year";

export interface ScheduleRow {
  date: string;
  label: string;
  amount: string;
}

export type CardPricing =
  | { kind: "one-time"; amount: Money }
  | { kind: "subscription"; amount: Money; interval: BillingInterval }
  | {
      kind: "trial";
      trialLabel: string;
      then: Money;
      interval: BillingInterval;
    }
  | {
      kind: "initial-plus-subscription";
      today: Money;
      then: Money;
      interval: BillingInterval;
    }
  | {
      kind: "trial-plus-initial-plus-subscription";
      trialLabel: string;
      initial: Money;
      then: Money;
      interval: BillingInterval;
      schedule: ScheduleRow[];
    };

interface PurchaseCardView {
  pill: { label: string; tone: "accent" | "neutral"; icon: SvgrIcon };
  headline: string;
  headlineSuffix?: string;
  secondaryLines: string[];
  inlineNote?: string;
  ctaLabel: string;
  disclaimer?: string;
  schedule?: ScheduleRow[];
}

// Only the monthly copy is drawn in the Figma file. The other cadences take
// their nouns and adjectives from the checkout (recurrenceWords in
// apps/checkout), so the landing and the checkout name a plan the same way.
const INTERVAL_COPY: Record<
  BillingInterval,
  {
    unit: string;
    per: string;
    adjective: string;
    afterFirst: string;
    firstCharge: string;
  }
> = {
  day: {
    unit: "día",
    per: "al día",
    adjective: "diaria",
    afterFirst: "Después del primer día",
    firstCharge: "Primer cobro diario",
  },
  week: {
    unit: "semana",
    per: "a la semana",
    adjective: "semanal",
    afterFirst: "Después de la primera semana",
    firstCharge: "Primer cobro semanal",
  },
  month: {
    unit: "mes",
    per: "al mes",
    adjective: "mensual",
    afterFirst: "Después del primer mes",
    firstCharge: "Primera mensualidad",
  },
  quarter: {
    unit: "trimestre",
    per: "al trimestre",
    adjective: "trimestral",
    afterFirst: "Después del primer trimestre",
    firstCharge: "Primer cobro trimestral",
  },
  "half-year": {
    unit: "semestre",
    per: "al semestre",
    adjective: "semestral",
    afterFirst: "Después del primer semestre",
    firstCharge: "Primer cobro semestral",
  },
  year: {
    unit: "año",
    per: "al año",
    adjective: "anual",
    afterFirst: "Después del primer año",
    firstCharge: "Primer cobro anual",
  },
};

// Locale per currency, as the checkout formats amounts: MXN groups with a
// comma ("1,234.56"), COP with a dot ("1.234"), so the card and the payment
// page never disagree on the same number.
const LOCALE_BY_CURRENCY: Record<string, string> = {
  USD: "en-US",
  MXN: "es-MX",
  COP: "es-CO",
};

const formatMoney = ({ amount, currency }: Money): string => {
  const fractionDigits = currency === "USD" || amount % 1 !== 0 ? 2 : 0;
  const formatted = new Intl.NumberFormat(
    LOCALE_BY_CURRENCY[currency] ?? "es-CO",
    {
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    }
  ).format(amount);
  return `${currency} ${formatted}`;
};

export const describePricing = (pricing: CardPricing): PurchaseCardView => {
  switch (pricing.kind) {
    case "one-time":
      return {
        pill: { label: "Pago único", tone: "accent", icon: Ico.CreditCard },
        headline: formatMoney(pricing.amount),
        secondaryLines: [],
        inlineNote: "Pago único",
        ctaLabel: "Comprar ahora",
      };

    case "subscription": {
      const copy = INTERVAL_COPY[pricing.interval];
      return {
        // The designed pill for this variant is neutral, while the otherwise
        // identical one on "initial-plus-subscription" is accent. Unify here
        // once design rules on it.
        pill: {
          label: `Suscripción ${copy.adjective}`,
          tone: "neutral",
          icon: Ico.ArrowsRotate,
        },
        headline: formatMoney(pricing.amount),
        headlineSuffix: `/${copy.unit}`,
        secondaryLines: [],
        inlineNote: `Cobro ${copy.adjective} recurrente`,
        ctaLabel: "Suscribirme",
        disclaimer: `Se cobrará ${copy.adjective}mente mientras la suscripción esté activa.`,
      };
    }

    case "trial": {
      const copy = INTERVAL_COPY[pricing.interval];
      return {
        pill: { label: "Prueba gratis", tone: "accent", icon: Ico.Gift },
        headline: pricing.trialLabel,
        secondaryLines: [`Luego, ${formatMoney(pricing.then)} / ${copy.unit}`],
        ctaLabel: "Comenzar prueba gratis",
        disclaimer:
          "No se realizará ningún cobro hoy. Cancela antes de que finalice la prueba para evitar el primer cobro.",
      };
    }

    case "initial-plus-subscription": {
      const copy = INTERVAL_COPY[pricing.interval];
      return {
        pill: {
          label: `Suscripción ${copy.adjective}`,
          tone: "accent",
          icon: Ico.ArrowsRotate,
        },
        headline: `${formatMoney(pricing.today)} hoy`,
        secondaryLines: [`Luego, ${formatMoney(pricing.then)} / ${copy.unit}`],
        ctaLabel: "Suscribirme",
        disclaimer: `${
          copy.afterFirst
        }, tu suscripción se renovará por ${formatMoney(pricing.then)} ${
          copy.per
        }.`,
      };
    }

    case "trial-plus-initial-plus-subscription": {
      const copy = INTERVAL_COPY[pricing.interval];
      return {
        pill: { label: "Prueba gratis", tone: "accent", icon: Ico.Gift },
        headline: pricing.trialLabel,
        secondaryLines: [
          `Luego, tarifa inicial de ${formatMoney(pricing.initial)}`,
          `Después, ${formatMoney(pricing.then)} / ${copy.unit}`,
        ],
        ctaLabel: "Comenzar prueba gratis",
        schedule: pricing.schedule,
        disclaimer: `Tu suscripción se renovará automáticamente por ${formatMoney(
          pricing.then
        )} ${copy.per} hasta que la canceles.`,
      };
    }
  }
};

export interface SubscriptionPlanInput {
  recurrenceUnit: string;
  recurrenceCount: number;
  price: number;
  initialPrice?: number | null;
  currency: string;
  trialDays?: number | null;
  trialAvailable?: boolean;
}

// A fixed table rather than Intl: ICU spells September "sept" and es-CO adds
// "de", while the design reads "26 sep 2026".
const SCHEDULE_MONTHS = [
  "ene",
  "feb",
  "mar",
  "abr",
  "may",
  "jun",
  "jul",
  "ago",
  "sep",
  "oct",
  "nov",
  "dic",
];

const formatScheduleDate = (date: Date): string =>
  `${date.getDate()} ${SCHEDULE_MONTHS[date.getMonth()]} ${date.getFullYear()}`;

const intervalFor = (unit: string, count: number): BillingInterval | null => {
  if (unit === "DAY" && count === 1) return "day";
  if (unit === "WEEK" && count === 1) return "week";
  if (unit === "MONTH" && count === 1) return "month";
  if (unit === "MONTH" && count === 3) return "quarter";
  if (unit === "MONTH" && count === 6) return "half-year";
  if (unit === "YEAR" && count === 1) return "year";
  return null;
};

// The day-counted cadences are excluded: they are added as days, not months.
const CYCLE_MONTHS: Record<Exclude<BillingInterval, "day" | "week">, number> = {
  month: 1,
  quarter: 3,
  "half-year": 6,
  year: 12,
};

const addDays = (date: Date, days: number): Date => {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
};

// Clamped so a charge due on the 31st lands on the last day of a shorter month
// instead of rolling into the one after.
const addMonths = (date: Date, months: number): Date => {
  const next = new Date(date);
  next.setDate(1);
  next.setMonth(next.getMonth() + months);
  const lastDay = new Date(
    next.getFullYear(),
    next.getMonth() + 1,
    0
  ).getDate();
  next.setDate(Math.min(date.getDate(), lastDay));
  return next;
};

const addCycle = (date: Date, interval: BillingInterval): Date =>
  interval === "day"
    ? addDays(date, 1)
    : interval === "week"
    ? addDays(date, 7)
    : addMonths(date, CYCLE_MONTHS[interval]);

const dayCount = (days: number): string =>
  `${days} ${days === 1 ? "día" : "días"}`;

/**
 * The card variant that describes an offered plan, or null for a recurrence
 * outside the six the hub offers.
 *
 * The trial and the initial fee are read independently, so a plan carrying both
 * renders the trial + initial + subscription variant even though the hub does
 * not create such plans yet. A buyer whose trial is used up sees the plan
 * without it.
 */
export const cardPricingFromPlan = (
  plan: SubscriptionPlanInput,
  now: Date = new Date()
): CardPricing | null => {
  const interval = intervalFor(plan.recurrenceUnit, plan.recurrenceCount);
  if (!interval) return null;

  const then: Money = { amount: plan.price, currency: plan.currency };
  const trialDays = plan.trialAvailable === false ? 0 : plan.trialDays ?? 0;
  const initial: Money | null =
    plan.initialPrice != null && plan.initialPrice !== plan.price
      ? { amount: plan.initialPrice, currency: plan.currency }
      : null;

  if (trialDays > 0 && initial) {
    const copy = INTERVAL_COPY[interval];
    const initialChargeAt = addDays(now, trialDays);
    const firstRenewalAt = addCycle(initialChargeAt, interval);
    return {
      kind: "trial-plus-initial-plus-subscription",
      trialLabel: `${dayCount(trialDays)} gratis`,
      initial,
      then,
      interval,
      schedule: [
        {
          date: "Hoy",
          label: `Comienza tu prueba de ${dayCount(trialDays)}`,
          amount: `${plan.currency} 0`,
        },
        {
          date: formatScheduleDate(initialChargeAt),
          label: "Tarifa inicial · pago único",
          amount: formatMoney(initial),
        },
        {
          date: formatScheduleDate(firstRenewalAt),
          label: copy.firstCharge,
          amount: `${formatMoney(then)}/${copy.unit}`,
        },
      ],
    };
  }

  if (trialDays > 0) {
    return {
      kind: "trial",
      trialLabel: `${dayCount(trialDays)} gratis`,
      then,
      interval,
    };
  }
  if (initial) {
    return {
      kind: "initial-plus-subscription",
      today: initial,
      then,
      interval,
    };
  }
  return { kind: "subscription", amount: then, interval };
};

/** The plan's name, or "Plan mensual" and the like when the mentor left it blank. */
export const planTitle = (
  plan: Pick<SubscriptionPlanInput, "recurrenceUnit" | "recurrenceCount"> & {
    name?: string | null;
  }
): string => {
  if (plan.name) return plan.name;
  const interval = intervalFor(plan.recurrenceUnit, plan.recurrenceCount);
  return interval
    ? `Plan ${INTERVAL_COPY[interval].adjective}`
    : "Plan de suscripción";
};

interface PlanPriceInCurrency {
  currency: string;
  price: number;
  initialAmount: number;
  initialPrice?: number | null;
}

/**
 * The plan a landing features, in the visitor's currency: the first plan the
 * card can describe (the API lists them cheapest first), priced in `currency`,
 * else in USD, else in the first currency it is offered in. Null when none of
 * the plans has card copy.
 */
export const featuredPlanIn = <
  T extends SubscriptionPlanInput & {
    initialAmount: number;
    pricesByCurrency: PlanPriceInCurrency[];
  }
>(
  plans: T[],
  currency: string
): T | null => {
  const plan = plans.find((p) => cardPricingFromPlan(p) !== null);
  if (!plan) return null;
  const local =
    plan.pricesByCurrency.find((p) => p.currency === currency) ??
    plan.pricesByCurrency.find((p) => p.currency === "USD") ??
    plan.pricesByCurrency[0];
  return local
    ? {
        ...plan,
        currency: local.currency,
        price: local.price,
        initialAmount: local.initialAmount,
        initialPrice: local.initialPrice ?? null,
      }
    : plan;
};
